import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiShare2 } from 'react-icons/fi';
import AppModal from '../../common/AppModal';
import services from '../../services';

// Per-user setting: which boards / lists this user may share tickets to (Choose Board → "Share with another board").
// The user does not need to be a member of those boards. Nothing ticked = this user cannot share. Admins can always share.
// Brand green tick boxes (same green as the app's buttons)
const checkboxStyle = { accentColor: '#1a7d3e', cursor: 'pointer' };

const TicketSharingModal = ({ user, onClose }) => {
    const [boards, setBoards] = useState([]);
    // { [boardId]: '*' | Set(listIds) }
    const [picked, setPicked] = useState({});
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let alive = true;
        Promise.all([services.board.GET_BOARD_LIST(), services.taskShare.GET_SETTING(user.id)])
            .then(([b, s]) => {
                if (!alive) return;
                setBoards(
                    (b?.data?.data || [])
                        .filter((x) => x.status !== false)
                        .map((x) => ({ ...x, BoardLists: (x.BoardLists || []).filter((l) => l.status !== false) })),
                );
                const next = {};
                (s?.data?.data || []).forEach((t) => {
                    next[t.boardId] = t.listIds.includes('*') ? '*' : new Set(t.listIds);
                });
                setPicked(next);
            })
            .catch(() => toast.error('Could not load the boards'))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [user.id]);

    const toggleBoard = (boardId) =>
        setPicked((prev) => {
            const next = { ...prev };
            if (next[boardId]) delete next[boardId];
            else next[boardId] = '*';
            return next;
        });

    const toggleList = (board, listId) =>
        setPicked((prev) => {
            const allIds = (board.BoardLists || []).map((l) => l.id);
            const current = prev[board.id] === '*' ? new Set(allIds) : new Set(prev[board.id] || []);
            if (current.has(listId)) current.delete(listId);
            else current.add(listId);
            const next = { ...prev };
            if (!current.size) delete next[board.id];
            else next[board.id] = current.size === allIds.length ? '*' : current;
            return next;
        });

    const save = async () => {
        setSaving(true);
        try {
            const targets = Object.entries(picked).map(([boardId, v]) => ({ boardId, listIds: v === '*' ? ['*'] : [...v] }));
            await services.taskShare.SET_SETTING(user.id, targets);
            toast.success('Ticket sharing saved');
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Could not save');
        } finally {
            setSaving(false);
        }
    };

    const term = search.trim().toLowerCase();
    const shown = boards.filter(
        (b) => !term || b.boardName?.toLowerCase().includes(term) || (b.BoardLists || []).some((l) => l.coloum?.toLowerCase().includes(term)),
    );
    const boardCount = Object.keys(picked).length;

    return (
        <AppModal
            show
            onClose={onClose}
            icon={<FiShare2 />}
            title="Ticket sharing"
            subtitle={`Boards and lists ${user.name || user.email} may share tickets to`}
            maxWidth="560px"
            footer={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>
                        {boardCount ? `${boardCount} board(s) selected` : 'Nothing selected = this user cannot share tickets'}
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className="btn btn-light" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={saving || loading}
                            onClick={save}
                            style={{ background: '#1a7d3e', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', fontWeight: 600 }}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>
            }
        >
            <p style={{ fontSize: 12, color: '#6b7280', marginTop: 0 }}>
                The user does not need to be a member of these boards. A shared ticket stays on its own board and list too. Admins can always share to every board.
            </p>
            <input
                type="text"
                placeholder="Search boards or lists"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="form-control mb-2"
                style={{ fontSize: 13 }}
            />
            {loading ? (
                <div style={{ padding: 16, color: '#6b7280' }}>Loading…</div>
            ) : (
                <div style={{ maxHeight: '50vh', overflowY: 'auto' }} data-testid="ticket-sharing-boards">
                    {shown.map((b) => {
                        const value = picked[b.id];
                        const lists = b.BoardLists || [];
                        const isOn = (id) => value === '*' || (value instanceof Set && value.has(id));
                        return (
                            <div key={b.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13, margin: 0 }}>
                                    <input type="checkbox" checked={!!value} onChange={() => toggleBoard(b.id)} style={checkboxStyle} />
                                    {b.boardName}
                                    {value && (
                                        <span style={{ fontWeight: 400, fontSize: 11, color: '#1a7d3e' }}>
                                            {value === '*' ? 'all lists' : `${value.size} of ${lists.length} lists`}
                                        </span>
                                    )}
                                </label>
                                {value && lists.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', padding: '6px 0 0 24px' }}>
                                        {lists.map((l) => (
                                            <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, margin: 0 }}>
                                                <input type="checkbox" checked={isOn(l.id)} onChange={() => toggleList(b, l.id)} style={checkboxStyle} />
                                                {l.coloum}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {!shown.length && <div style={{ padding: 16, color: '#6b7280' }}>No boards found</div>}
                </div>
            )}
        </AppModal>
    );
};

export default TicketSharingModal;
